import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  Alert,
  Collapse,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Badge,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  OpenInNew as OpenIcon,
  Add as ExpandIcon,
  Remove as CollapseIcon,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { Task, TaskStatus, TaskPriority, TaskCategory } from '../../types';
import TaskDetailsModal from './TaskDetailsModal';
import PatientClinicalStrip, { PatientClinicalStripHandle } from '../PatientDetails/PatientClinicalStrip';
import { triageApi } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { formatDateTime } from '../../utils/dateTime';
import { turnChip } from './turnChip';
import { mergeBoardLists } from './boardLists';

interface TriageBoardProps {
  viewMode: 'status' | 'category';
  modalityFilter: string;
  statusFilter: string;
  searchQuery: string;
  /** How many loaded tasks are the office's turn — the Needs reply filter shows it. */
  onNeedsReply?: (count: number) => void;
}

// Empty initial state - will be populated from API

const priorityColors: Record<TaskPriority, string> = {
  urgent: '#dc2626',
  high: '#f59e0b',
  medium: '#3b82f6',
  low: '#10b981',
};

const statusColumns: { id: TaskStatus; title: string; color: string; bgColor: string }[] = [
  { id: 'todo', title: 'To Do', color: '#ef4444', bgColor: '#fce4ec' },           // Pink post-it
  { id: 'inprogress', title: 'In Progress', color: '#f59e0b', bgColor: '#fff3e0' }, // Orange/peach post-it
  { id: 'completed', title: 'Completed', color: '#10b981', bgColor: '#e8f5e9' },   // Green post-it
];

const categoryColumns: { id: TaskCategory; title: string; color: string; bgColor: string }[] = [
  { id: 'clinical', title: 'Clinical', color: '#ef4444', bgColor: '#fce4ec' },      // Pink post-it
  { id: 'admin', title: 'Admin', color: '#3b82f6', bgColor: '#fff8e1' },            // Yellow post-it
  { id: 'appointment', title: 'Appointment', color: '#8b5cf6', bgColor: '#fffde7' }, // Light yellow post-it
  { id: 'general', title: 'General', color: '#6b7280', bgColor: '#e8f5e9' },        // Green post-it
];

const TriageBoard: React.FC<TriageBoardProps> = ({
  viewMode,
  modalityFilter,
  statusFilter,
  searchQuery,
  onNeedsReply,
}) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [collapsedColumns, setCollapsedColumns] = useState<Set<string>>(new Set(['completed'])); // Completed collapsed by default
  // Mobile: a segmented control picks one full-width column instead of the
  // side-by-side kanban (which overflows + breaks touch drag). down('md') per plan.
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileColumnId, setMobileColumnId] = useState<string>('');
  const completedOnly = viewMode === 'status' && statusFilter === 'completed';
  // On mobile the collapse mechanism is replaced by the segmented control, so
  // never render a column collapsed there. The Completed tab also keeps its
  // single column open — To Do / In Progress are not on screen.
  const colCollapsed = (id: string) => !isMobile && !completedOnly && collapsedColumns.has(id);
  const dateTimeFormat = useAuthStore((state) => state.user?.dateTimeFormat);
  const location = useLocation();
  const navigate = useNavigate();
  const [deepLinkHandled, setDeepLinkHandled] = useState(false);
  // A message written elsewhere and handed to this task to be sent from here.
  const [pendingDraft, setPendingDraft] = useState<string>('');
  const singleTaskTriedRef = useRef(false);

  // In-place patient chart: one hidden chart-surface host serves both entry
  // points (board-card name taps and the task dock). `nonce` makes each tap a
  // fresh object so re-opening the same patient still re-triggers the effect.
  const stripRef = useRef<PatientClinicalStripHandle>(null);
  const [chartTarget, setChartTarget] = useState<{
    spruceEntityId: string;
    name?: string;
    phone?: string;
    email?: string;
    nonce: number;
  } | null>(null);
  // Card name tap → the full Contacts profile (auto-selected patient +
  // "Back to task" that reopens this task's dock). Spruce-only ids fall back
  // to the legacy standalone page. The drawer stays for PT Details only.
  const openProfileForTask = (task: Task) => {
    const tid = (task as any).id || (task as any)._id;
    const returnState = { returnTo: `/?taskId=${tid}`, backLabel: 'Back to task' };
    if (task.internalPatientId) {
      navigate('/contacts', { state: { ...returnState, selectInternalId: task.internalPatientId } });
      return;
    }
    if (!task.patientId) return;
    const params = new URLSearchParams();
    if (task.patientName) params.set('name', task.patientName);
    if (task.patientPhone) params.set('phone', task.patientPhone);
    if (task.patientEmail) params.set('email', task.patientEmail);
    const query = params.toString();
    navigate(`/patient-details/${task.patientId}${query ? `?${query}` : ''}`, { state: returnState });
  };
  // Open via effect, not in the click handler: the strip closes its sheet when
  // spruceEntityId changes (its own reset effect runs first, as a child), so
  // opening here — after that reset — is the only ordering that sticks.
  useEffect(() => {
    if (chartTarget) stripRef.current?.openChart();
  }, [chartTarget]);

  const toggleColumnCollapse = (columnId: string) => {
    setCollapsedColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnId)) {
        newSet.delete(columnId);
      } else {
        newSet.add(columnId);
      }
      return newSet;
    });
  };

  // Fetch tasks from API - with smart updates to prevent visual jumps
  const fetchTasks = useCallback(async (isInitialLoad = false) => {
    try {
      // Only show loading skeleton on initial load, not on background polls
      if (isInitialLoad) {
        setIsLoading(true);
      }
      setError(null);
      
      // Bounded fetch: the unindexed sort on the full collection blows past the
      // 30s request timeout when unbounded (that's what was timing the board
      // out). Pull the most-recent page so the board loads reliably. Interim
      // stopgap — the real fix is a backend index on { lastMessageAt, createdAt }
      // (see the perf handoff note), after which this can go back to unbounded.
      const params: { limit: number; status?: string } = { limit: 100 };
      if (statusFilter === 'completed') {
        params.status = 'completed';
      }
      // Every in-progress task as well, in a second request: they are few,
      // and the 100 most recent alone drop them off the board on a busy day
      // (boardLists.ts). Not needed when only completed tasks are shown.
      const [response, inProgressResponse] = await Promise.all([
        triageApi.getTasks(params),
        statusFilter === 'completed'
          ? Promise.resolve(null)
          : triageApi.getTasks({ status: 'inprogress', limit: 500 }).catch(() => null),
      ]);
      
      // Handle response format: { success: true, data: [...] } or direct array
      const tasksData = response.data?.data || response.data || [];
      const recentTasks = Array.isArray(tasksData) ? tasksData : [];
      const inProgressData = inProgressResponse?.data?.data || inProgressResponse?.data || [];
      const newTasks = mergeBoardLists(recentTasks, Array.isArray(inProgressData) ? inProgressData : []);
      
      // Smart update: Only update state if data has actually changed
      // This prevents unnecessary re-renders and visual jumps
      setTasks(prevTasks => {
        // Compare by serializing - if identical, return previous state (no re-render)
        const prevJson = JSON.stringify(prevTasks.map(t => ({
          id: t.id,
          status: t.status,
          category: t.category,
          updatedAt: t.updatedAt,
          hasInternalNotes: t.hasInternalNotes,
          internalNotesCount: t.internalNotes?.length || 0,
          messageCount: t.messageCount || 0,
          turn: t.turn ? `${t.turn.who}|${t.turn.since}` : '',
        })));
        const newJson = JSON.stringify(newTasks.map(t => ({
          id: t.id,
          status: t.status,
          category: t.category,
          updatedAt: t.updatedAt,
          hasInternalNotes: t.hasInternalNotes,
          internalNotesCount: t.internalNotes?.length || 0,
          messageCount: t.messageCount || 0,
          turn: t.turn ? `${t.turn.who}|${t.turn.since}` : '',
        })));
        
        if (prevJson === newJson) {
          // No changes - return previous state to skip re-render
          return prevTasks;
        }
        
        // Data changed - update with new tasks
        return newTasks;
      });
    } catch (err: any) {
      console.error('Failed to fetch tasks:', err);
      // Only set error if we have no existing data
      setError(err.message || 'Failed to load tasks');
    } finally {
      if (isInitialLoad) {
        setIsLoading(false);
      }
    }
  }, [statusFilter]);

  // Fetch tasks on mount and set up polling for real-time updates
  useEffect(() => {
    // Initial load - show loading skeleton
    fetchTasks(true);

    // Poll for new tasks on an interval - silent updates (no loading indicator).
    // 20s (was 5s): easier on the shared dev DB, whose slow triage query was
    // tripping the 30s request timeout when polled aggressively. The real fix
    // is a backend index on the query; this just keeps the board calm.
    // Not while a task is open. The board is behind the modal where nobody can
    // see it, and this is the most expensive request in the app — 2-4 seconds
    // against the shared dev DB. The open task polls itself for about 570ms
    // instead, so keeping a task open now costs less than leaving the board up.
    const pollInterval = setInterval(() => {
      if (selectedTask) return;
      fetchTasks(false);
    }, 20000);

    return () => clearInterval(pollInterval);
  }, [fetchTasks, selectedTask]);

  // Whatever changed while the modal was covering the board, catch up once on
  // the way out rather than waiting up to another 20 seconds for it.
  const wasTaskOpen = useRef(false);
  useEffect(() => {
    if (wasTaskOpen.current && !selectedTask) fetchTasks(false);
    wasTaskOpen.current = Boolean(selectedTask);
  }, [selectedTask, fetchTasks]);

  // Filter tasks
  useEffect(() => {
    onNeedsReply?.(tasks.filter((t) => t.turn?.who === 'office').length);
  }, [tasks, onNeedsReply]);

  const filteredTasks = tasks.filter((task) => {
    // Signature faxes live in the Signatures drawer — not on clinical Todo columns.
    if (task.faxLane === 'signature' || (task.tags || []).includes('fax_signature')) {
      return false;
    }
    if (task.faxLane === 'filing' || (task.tags || []).includes('fax_filing')) {
      return false;
    }

    if (task.followUpDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const followUpDate = new Date(task.followUpDate);
      if (!Number.isNaN(followUpDate.getTime()) && followUpDate > today) {
        return false;
      }
    }
    if (modalityFilter !== 'all' && task.modality !== modalityFilter) return false;
    // "Needs reply" is a turn, not a status: whatever column it sits in.
    if (statusFilter === 'needs_reply') {
      if (task.turn?.who !== 'office') return false;
    } else if (statusFilter !== 'all' && task.status !== statusFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        task.patientName.toLowerCase().includes(query) ||
        task.patientPhone.includes(query) ||
        task.messageText.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const columns: { id: string; title: string; color: string; bgColor: string }[] =
    viewMode === 'status'
      ? (completedOnly ? statusColumns.filter((c) => c.id === 'completed') : statusColumns)
      : categoryColumns;
  // Active column on mobile (validated against the current view's columns).
  const activeMobileCol = columns.find((c) => c.id === mobileColumnId)?.id ?? columns[0]?.id;
  const boardColumns = isMobile ? columns.filter((c) => c.id === activeMobileCol) : columns;

  const getTasksForColumn = (columnId: string) => {
    return filteredTasks.filter((task) => {
      if (viewMode === 'status') {
        // Status View: show tasks by their status
        return task.status === columnId;
      } else {
        // Category View: show tasks by category, but EXCLUDE completed tasks
        // Completed tasks should only appear in the "Completed" column in Status View
        if (task.status === 'completed') {
          return false; // Don't show completed tasks in Category View
        }
        return task.category === columnId;
      }
    });
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;
    
    if (source.droppableId === destination.droppableId) return;

    const taskId = draggableId; // Keep as string for MongoDB ObjectId
    const newValue = destination.droppableId;

    // Optimistic update
    setTasks((prevTasks) =>
      prevTasks.map((task) => {
        if (task.id.toString() === taskId) {
          return viewMode === 'status'
            ? { ...task, status: newValue as TaskStatus }
            : { ...task, category: newValue as TaskCategory };
        }
        return task;
      })
    );

    // Update backend
    try {
      const updateField = viewMode === 'status' ? 'status' : 'category';
      await triageApi.updateTask(taskId, { [updateField]: newValue });
    } catch (err) {
      console.error('Failed to update task:', err);
      // Revert on error
      fetchTasks(false);
    }
  };

  const handleTaskClick = useCallback(async (task: Task) => {
    setSelectedTask(task);
    setIsModalOpen(true);
    try {
      const response = await triageApi.getTask(task.id);
      const fullTask = response.data?.data || response.data?.task || response.data;
      if (fullTask) {
        setSelectedTask(fullTask);
      }
    } catch (err) {
      console.error('Failed to load task details:', err);
    }
  }, []);

  useEffect(() => {
    if (deepLinkHandled) return;
    const params = new URLSearchParams(location.search);
    const taskId = params.get('taskId');
    if (!taskId) return;
    // A note handed over from cancelling an appointment. Carried in the URL so
    // the office lands on the task with the message already written.
    const handedOverDraft = params.get('draft') || '';
    setPendingDraft(handedOverDraft);
    const stripParam = () => {
      params.delete('taskId');
      params.delete('draft');
      navigate(
        { pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' },
        { replace: true },
      );
    };
    const match = tasks.find((task: any) => {
      const id = task?.id ?? task?._id;
      return id?.toString?.() === taskId;
    });
    if (match) {
      setDeepLinkHandled(true);
      handleTaskClick(match);
      stripParam();
      return;
    }
    // Only attempt the direct fetch once; if it fails we fall back to opening
    // the task from the board once it loads (rather than never opening it).
    if (singleTaskTriedRef.current) return;
    singleTaskTriedRef.current = true;
    // The board isn't loaded yet. Previously we waited for the whole board
    // (a slow, unindexed scan+sort) before "Back to task" could reopen the
    // dock. Fetch just this one task by id instead — the dock opens right
    // away and the board keeps loading behind it.
    (async () => {
      try {
        const res = await triageApi.getTask(taskId);
        const data = res.data?.data || res.data?.task || res.data;
        if (data) {
          setDeepLinkHandled(true);
          handleTaskClick({ ...data, id: data.id || data._id || taskId } as Task);
          stripParam();
        }
        // No data: leave the deep link unhandled so the board-load path below
        // can still open it once tasks arrive.
      } catch {
        // Same fallback on error — the board path will open it.
      }
    })();
  }, [deepLinkHandled, location.pathname, location.search, navigate, tasks, handleTaskClick]);

  const formatTime = (dateString: string) => {
    return formatDateTime(dateString, dateTimeFormat) || '';
  };

  if (isLoading && tasks.length === 0) {
    return (
      <Box sx={{ display: 'flex', gap: 2 }}>
        {columns.map((column) => (
          <Paper key={column.id} sx={{ flex: 1, p: 2 }}>
            <Skeleton variant="text" width="60%" height={32} />
            <Skeleton variant="rectangular" height={120} sx={{ mt: 2, borderRadius: 2 }} />
            <Skeleton variant="rectangular" height={120} sx={{ mt: 2, borderRadius: 2 }} />
          </Paper>
        ))}
      </Box>
    );
  }

  if (error && tasks.length === 0) {
    // Keep it calm and non-technical — the raw axios message (e.g. "timeout of
    // 30000ms exceeded") reads as a crash. This only shows on a failed first
    // load; once tasks are in, a failed poll silently keeps the last board.
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Reconnecting to the triage board… it will refresh automatically in a moment.
      </Alert>
    );
  }

  return (
    <>
      {/* Mobile: a segmented control selects which single column is shown
          full-width (replaces the side-by-side kanban that overflowed). */}
      {isMobile && (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={activeMobileCol}
          onChange={(_, v) => v && setMobileColumnId(v)}
          sx={{
            mb: 2,
            // Grid so every column label fits without horizontal scroll:
            // 4 columns wrap to 2x2, 3 columns sit in a single row.
            display: 'grid',
            gridTemplateColumns: columns.length >= 4 ? 'repeat(2, 1fr)' : `repeat(${columns.length || 1}, 1fr)`,
            gap: 1,
            '& .MuiToggleButton-root': {
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              textTransform: 'none',
              px: 1,
              gap: 0.75,
            },
          }}
        >
          {columns.map((column) => (
            <ToggleButton key={column.id} value={column.id}>
              {column.title}
              <Badge
                badgeContent={getTasksForColumn(column.id).length}
                color="primary"
                sx={{ '& .MuiBadge-badge': { position: 'static', transform: 'none' } }}
              />
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Box
          sx={{
            display: 'flex',
            gap: 1.25,
            overflowX: 'auto',
            pb: 1.25,
          }}
        >
          {boardColumns.map((column) => (
            <Droppable
              droppableId={column.id}
              key={column.id}
              isDropDisabled={false}
              isCombineEnabled={false}
              ignoreContainerClipping={false}
            >
              {(provided, snapshot) => (
                <Paper
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    flex: colCollapsed(column.id) ? '0 0 auto' : 1,
                    // On mobile a single column shows full-width and must be able
                    // to shrink to the viewport (flex items default to min-width
                    // auto, which otherwise keeps the column at its 300px content
                    // width and overflows the page).
                    minWidth: colCollapsed(column.id) ? 60 : isMobile ? 0 : 280,
                    maxWidth: isMobile && !colCollapsed(column.id) ? '100%' : undefined,
                    p: colCollapsed(column.id) ? 1 : { xs: 1, sm: 1.25 },
                    bgcolor: snapshot.isDraggingOver ? 'grey.200' : column.bgColor,
                    borderTop: `4px solid ${column.color}`,
                    transition: 'all 0.3s ease',
                  }}
                >
                  {/* Column Header */}
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: colCollapsed(column.id) ? 0 : 1.25,
                    gap: 1
                  }}>
                    {colCollapsed(column.id) ? (
                      // Collapsed header - vertical text
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        width: '100%',
                        gap: 1
                      }}>
                        <Tooltip title={`Expand ${column.title}`} placement="right">
                          <IconButton 
                            size="small" 
                            onClick={() => toggleColumnCollapse(column.id)}
                            sx={{ bgcolor: 'grey.100' }}
                          >
                            <ExpandIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Chip 
                          label={getTasksForColumn(column.id).length}
                          size="small"
                          sx={{ 
                            bgcolor: column.color,
                            color: 'white',
                            fontWeight: 600,
                          }}
                        />
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            writingMode: 'vertical-rl',
                            textOrientation: 'mixed',
                            fontWeight: 600,
                            color: 'text.secondary',
                            mt: 1
                          }}
                        >
                          {column.title}
                        </Typography>
                      </Box>
                    ) : (
                      // Expanded header
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {column.title}
                          </Typography>
                          <Chip 
                            label={getTasksForColumn(column.id).length}
                            size="small"
                            sx={{ 
                              bgcolor: column.color,
                              color: 'white',
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                        {/* Only show collapse button for completed column */}
                        {column.id === 'completed' && !completedOnly && (
                          <Tooltip title="Collapse column">
                            <IconButton 
                              size="small" 
                              onClick={() => toggleColumnCollapse(column.id)}
                            >
                              <CollapseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </Box>

                  {/* Tasks - hidden when collapsed */}
                  <Collapse in={!colCollapsed(column.id)} timeout={300}>
                    <Box sx={{ minHeight: 200 }}>
                    {getTasksForColumn(column.id).length === 0 ? (
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        No tasks in this column
                      </Alert>
                    ) : (
                      getTasksForColumn(column.id).map((task, index) => {
                        const hasInternalNotes = !!task.hasInternalNotes ||
                          (Array.isArray(task.internalNotes) && task.internalNotes.length > 0) ||
                          (Array.isArray(task.conversationMessages) &&
                            task.conversationMessages.some((msg: any) =>
                              msg?.isInternalNote ||
                              msg?.messageType === 'internal_note' ||
                              msg?.source === 'spruce_internal' ||
                              msg?.direction === 'none' ||
                              ((msg?.direction === 'inbound' || msg?.direction === 'none') && msg?.senderName === 'Staff')
                            ));
                        return (
                        <Draggable
                          key={task.id}
                          draggableId={task.id.toString()}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <Card
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              sx={{
                                mb: 1,
                                cursor: 'pointer',
                                position: 'relative',
                                boxShadow: snapshot.isDragging
                                  ? '0 8px 16px rgba(0,0,0,0.2)'
                                  : '0 1px 3px rgba(0,0,0,0.1)',
                                transform: snapshot.isDragging
                                  ? 'rotate(3deg)'
                                  : 'none',
                                // Smooth transitions for seamless updates
                                transition: 'box-shadow 0.2s, transform 0.2s, opacity 0.3s ease-in-out',
                                borderLeft: `4px solid ${priorityColors[task.priority]}`,
                                // Subtle animation for new cards
                                animation: 'fadeIn 0.3s ease-in-out',
                                '@keyframes fadeIn': {
                                  '0%': { opacity: 0.7, transform: 'translateY(-5px)' },
                                  '100%': { opacity: 1, transform: 'translateY(0)' },
                                },
                                '&:hover': {
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                },
                              }}
                              onClick={() => handleTaskClick(task)}
                            >
                              {hasInternalNotes && (
                                <Tooltip title="Internal note on task">
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 8,
                                      right: 34,
                                      width: 10,
                                      height: 10,
                                      borderRadius: '50%',
                                      bgcolor: '#e53935',
                                      boxShadow: '0 0 0 2px #fff',
                                      zIndex: 1,
                                    }}
                                  />
                                </Tooltip>
                              )}
                              <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                                {/* Header: Name + Open button inline */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                  <Box>
                                    {/* Linked patients: the name routes to the full
                                        Contacts profile with Back to task. */}
                                    {(task.internalPatientId || task.patientId) ? (
                                      <Tooltip title="Open patient profile">
                                        <Typography
                                          onClick={(e) => { e.stopPropagation(); openProfileForTask(task); }}
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: '0.82rem',
                                            lineHeight: 1.3,
                                            cursor: 'pointer',
                                            color: 'primary.main',
                                            '&:hover': { textDecoration: 'underline' },
                                          }}
                                        >
                                          {task.patientName}
                                        </Typography>
                                      </Tooltip>
                                    ) : (
                                      <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', lineHeight: 1.3 }}>
                                        {task.patientName}
                                      </Typography>
                                    )}
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                      {task.patientPhone}
                                    </Typography>
                                    {/* Whose turn it is — the server's call, worded here.
                                        A caption with a dot, not a pill: most open cards
                                        are the office's turn, so the words stay quiet and
                                        only a wait of a day or more takes colour. */}
                                    {(() => {
                                      const chip = turnChip(task.turn);
                                      return chip ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.4 }}>
                                          <Box
                                            aria-hidden
                                            sx={{
                                              width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                                              bgcolor: chip.tone === 'office' ? '#f59e0b' : 'transparent',
                                              border: chip.tone === 'office' ? 'none' : '1.5px solid',
                                              borderColor: 'text.disabled',
                                            }}
                                          />
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontSize: '0.7rem', lineHeight: 1.3, fontWeight: 600,
                                              color: chip.tone === 'office' && chip.aged ? '#b45309' : 'text.secondary',
                                            }}
                                          >
                                            {chip.label}
                                          </Typography>
                                        </Box>
                                      ) : null;
                                    })()}
                                    {task.proxyIntake?.active && (
                                      <Chip
                                        size="small"
                                        color={task.proxyIntake?.unresolved ? 'warning' : 'default'}
                                        variant={task.proxyIntake?.unresolved ? 'filled' : 'outlined'}
                                        label={task.proxyIntake?.unresolved ? 'Proxy · needs patient' : 'Proxy'}
                                        sx={{ mt: 0.35, height: 20, fontSize: '0.65rem' }}
                                        onClick={(e) => { e.stopPropagation(); handleTaskClick(task); }}
                                      />
                                    )}
                                    {(task.faxLane === 'clinical' || (task.tags || []).includes('fax_clinical')) && (
                                      <Chip
                                        size="small"
                                        color={(task.tags || []).includes('needs_patient') || !task.internalPatientId ? 'warning' : 'info'}
                                        variant="outlined"
                                        label={(task.tags || []).includes('needs_patient') || !task.internalPatientId ? 'Fax · needs patient' : 'Fax · clinical'}
                                        sx={{ mt: 0.35, height: 20, fontSize: '0.65rem', ml: 0.35 }}
                                        onClick={(e) => { e.stopPropagation(); handleTaskClick(task); }}
                                      />
                                    )}
                                  </Box>
                                  <IconButton 
                                    size="small" 
                                    onClick={(e) => { e.stopPropagation(); handleTaskClick(task); }}
                                    sx={{ p: 0.5, mt: -0.5, mr: -0.5 }}
                                  >
                                    <OpenIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Box>

                                {/* AI Key Points - flex layout allows short items on same line */}
                                {task.aiAnalysis?.keyPoints && task.aiAnalysis.keyPoints.length > 0 && (
                                  <Box sx={{ 
                                    mt: 0.5, 
                                    display: 'flex', 
                                    flexWrap: 'wrap',
                                    gap: '2px 8px',
                                    alignItems: 'flex-start'
                                  }}>
                                    {task.aiAnalysis.keyPoints.slice(0, 4).map((point: string, idx: number) => (
                                      <Typography 
                                        key={idx} 
                                        variant="caption" 
                                        color="text.secondary" 
                                        sx={{
                                          lineHeight: 1.4,
                                          maxWidth: '100%',
                                          wordBreak: 'break-word',
                                        }}
                                      >
                                        • {point}
                                      </Typography>
                                    ))}
                                    {task.aiAnalysis.keyPoints.length > 4 && (
                                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                                        • +{task.aiAnalysis.keyPoints.length - 4} more
                                      </Typography>
                                    )}
                                  </Box>
                                )}

                                {/* Footer: msgs count + time */}
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                  {task.messageCount && task.messageCount > 1 
                                    ? `${task.messageCount} msgs - ${formatTime(task.lastMessageAt || task.updatedAt || task.createdAt)}`
                                    : formatTime(task.lastMessageAt || task.updatedAt || task.createdAt)
                                  }
                                </Typography>

                                {/* Modality tag removed - column already indicates category */}
                              </CardContent>
                            </Card>
                          )}
                        </Draggable>
                      );
                      })
                    )}
                    {provided.placeholder}
                    </Box>
                  </Collapse>
                </Paper>
              )}
            </Droppable>
          ))}
        </Box>
      </DragDropContext>

      {/* Task Details Modal */}
      <TaskDetailsModal
        // Keyed by the task so switching patients builds a fresh panel rather
        // than carrying the last one's state across. This modal stays mounted
        // between tasks, and a booking link left in state showed one patient's
        // live link — and its Withdraw button — on the next patient's panel.
        key={String((selectedTask as any)?.id ?? 'none')}
        open={isModalOpen}
        task={selectedTask}
        initialResponse={pendingDraft}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTask(null);
        }}
        onUpdate={(updatedTask) => {
          setTasks((prev) =>
            prev.map((t) => (t.id === updatedTask.id ? updatedTask : t))
          );
          setSelectedTask((prev) => (prev && prev.id === updatedTask.id ? updatedTask : prev));
        }}
        onDeleted={(taskId) => {
          // A deleted task has to leave the board; onUpdate maps over the list
          // and could only ever replace one.
          setTasks((prev) => prev.filter((t) => String(t.id) !== String(taskId)));
          setSelectedTask(null);
        }}
        onOpenChart={(target) => setChartTarget({ ...target, nonce: Date.now() })}
      />

      {/* Hidden chart-surface host: the same chart drawer Contacts uses,
          raised above the task Dialog (modal z-index) so it opens in place
          over an open task. */}
      <PatientClinicalStrip
        ref={stripRef}
        chartSurface
        spruceEntityId={chartTarget?.spruceEntityId}
        patientName={chartTarget?.name}
        patientPhone={chartTarget?.phone}
        patientEmail={chartTarget?.email}
        drawerZIndex={theme.zIndex.modal + 2}
      />
    </>
  );
};

export default TriageBoard;

